import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormHelperText,
	Grid,
	OutlinedInput,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { FormField } from '../../../../form/FormField';
import { useTypedForm } from '../../../../../../shared/fe/hooks/useForm';
import { useModalContext } from '../../../../modal/ModalContext';
import { getNightOrDayPalette } from '../../../../utils/getNightOrDayPalette';

const createOrganizationModalSchema = z.object({});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.organization.settings.create.error.empty-name' })),
		url: z.string().nonempty(intl.formatMessage({ id: 'form.organization.settings.create.error.empty-url' })),
	});

type Props = z.infer<typeof createOrganizationModalSchema>;

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

const OrganizationCreate: FC<Props> = () => {
	const intl = useIntl();
	const theme = useTheme();
	const { closeModal } = useModalContext();

	const form = useTypedForm({
		schema: createFormSchema(intl),
	});

	const { formState, reset, handleSubmit } = form;

	useEffect(() => {
		reset({
			name: '',
			url: '',
		});
	}, [reset]);

	const onSubmit = (data: FormValues) => {
		return null;
	};

	return (
		<form noValidate onSubmit={handleSubmit(onSubmit)}>
			<DialogTitle>
				<FormattedMessage id={'form.organization.settings.create.title'} />
			</DialogTitle>
			<Divider />
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Stack gap={3}>
							<FormField
								form={form}
								name={'name'}
								label={intl.formatMessage({ id: 'form.organization.settings.create.name' })}
								render={(props) => <OutlinedInput {...props} />}
							/>
							<FormField
								form={form}
								name={'url'}
								label={intl.formatMessage({ id: 'form.organization.settings.create.url' })}
								render={(props) => (
									<OutlinedInput
										{...form.register('url')}
										{...props}
										sx={{ overflow: 'hidden' }}
										endAdornment={
											<Box
												display={'flex'}
												paddingX={1.5}
												position={'absolute'}
												right={0}
												height={'100%'}
												alignItems={'center'}
												border={`1px solid ${theme.palette[getNightOrDayPalette(theme)][300]}`}
												sx={{ backgroundColor: theme.palette[getNightOrDayPalette(theme)][50] }}
											>
												<Typography color={theme.palette[getNightOrDayPalette(theme)][500]}>
													<FormattedMessage
														id={'form.organization.settings.create.url-adornment'}
														values={{ environment: import.meta.env.NODE_ENV === 'production' ? 'app' : 'dev' }}
													/>
												</Typography>
											</Box>
										}
									/>
								)}
							/>
						</Stack>
					</Grid>

					{formState.errors.root != null && (
						<Grid item xs={12}>
							<FormHelperText error>{formState.errors.root.message}</FormHelperText>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<Divider />
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
					<Button color={'secondary'} onClick={closeModal}>
						<FormattedMessage id={'common.cancel'} />
					</Button>
					<Button type={'submit'} variant={'contained'} disabled={formState.isSubmitting}>
						<FormattedMessage id={'common.create'} />
					</Button>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { OrganizationCreate, createOrganizationModalSchema };
